/**
 * Admin Dashboard Shared Components and Utilities
 */
import { motion } from 'framer-motion';

export const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Neon color palette for icons
 */
export const NEON_COLORS = {
    cyan: { color: '#00FFFF', glow: '0 0 10px #00FFFF, 0 0 20px #00FFFF, 0 0 30px #00FFFF' },
    magenta: { color: '#FF00FF', glow: '0 0 10px #FF00FF, 0 0 20px #FF00FF, 0 0 30px #FF00FF' },
    lime: { color: '#39FF14', glow: '0 0 10px #39FF14, 0 0 20px #39FF14, 0 0 30px #39FF14' },
    pink: { color: '#FF1493', glow: '0 0 10px #FF1493, 0 0 20px #FF1493, 0 0 30px #FF1493' },
    blue: { color: '#00BFFF', glow: '0 0 10px #00BFFF, 0 0 20px #00BFFF, 0 0 30px #00BFFF' },
    purple: { color: '#BF00FF', glow: '0 0 10px #BF00FF, 0 0 20px #BF00FF, 0 0 30px #BF00FF' },
    orange: { color: '#FF6600', glow: '0 0 10px #FF6600, 0 0 20px #FF6600, 0 0 30px #FF6600' },
    yellow: { color: '#FFFF00', glow: '0 0 10px #FFFF00, 0 0 20px #FFFF00, 0 0 30px #FFFF00' },
    red: { color: '#FF0040', glow: '0 0 10px #FF0040, 0 0 20px #FF0040, 0 0 30px #FF0040' },
    green: { color: '#00FF7F', glow: '0 0 10px #00FF7F, 0 0 20px #00FF7F, 0 0 30px #00FF7F' },
};

/**
 * Neon Icon Component with glow effect and hover animation
 */
export const NeonIcon = ({ icon: Icon, neonColor = 'cyan', color = null, size = 'w-6 h-6', className = '', animate = true }) => {
    // Handle both neonColor (string key) and color (object from NEON_COLORS)
    let neon;
    if (color && typeof color === 'object' && color.color) {
        // color is a NEON_COLORS object
        neon = color;
    } else if (typeof neonColor === 'string') {
        // neonColor is a string key
        neon = NEON_COLORS[neonColor] || NEON_COLORS.cyan;
    } else {
        neon = NEON_COLORS.cyan;
    }
    
    // Handle size - could be a number or a className string
    const sizeClass = typeof size === 'number' ? `w-[${size}px] h-[${size}px]` : size;
    
    return (
        <motion.span
            className={`inline-flex ${className || ''}`}
            whileHover={animate ? { 
                scale: 1.15,
                filter: `drop-shadow(0 0 6px ${neon.color}) drop-shadow(0 0 12px ${neon.color}) drop-shadow(0 0 18px ${neon.color})`
            } : {}}
            transition={{ type: "spring", stiffness: 400, damping: 15 }}
            style={{ cursor: animate ? 'pointer' : 'default' }}
        >
            <Icon 
                className={sizeClass}
                style={{ 
                    color: neon.color,
                    filter: `drop-shadow(0 0 4px ${neon.color}) drop-shadow(0 0 8px ${neon.color})`,
                    transition: 'filter 0.3s ease'
                }}
            />
        </motion.span>
    );
};

/**
 * Bento Grid Card Component
 */
export const BentoCard = ({ children, className = '', span = 1, highlight = false, onClick = null }) => (
    <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        onClick={onClick}
        className={`
            rounded-2xl p-4 
            ${highlight ? 'bg-gradient-to-br from-red-500/20 to-red-900/10 border-red-500/30' : 'bg-black/40 border-primary/20'}
            border backdrop-blur-sm
            ${span === 2 ? 'col-span-2' : ''}
            ${className || ''}
        `}
    >
        {children}
    </motion.div>
);

/**
 * Stat Display Component with optional neon icon
 */
export const StatDisplay = ({ label, value, icon: Icon, trend = null, color = 'primary', neonColor = null }) => (
    <div className="flex items-center justify-between">
        <div>
            <p className="text-xs text-white/50 uppercase tracking-wider">{label}</p>
            <p className={`text-2xl font-bold text-${color}`}>{value}</p>
            {trend && (
                <p className={`text-xs ${trend > 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {trend > 0 ? '+' : ''}{trend}% vs yesterday
                </p>
            )}
        </div>
        {Icon && (neonColor ? (
            <NeonIcon icon={Icon} neonColor={neonColor} size="w-8 h-8" />
        ) : (
            <Icon className={`w-8 h-8 text-${color}/50`} />
        ))}
    </div>
);
